/**
 * Checkbox Component
 * Styled checkbox input with label. `size="lg"` is the touch variant
 * for the open-house kiosk: a 56 px row with a 28 px box and 18 px label.
 */

import { forwardRef } from 'react';
import { Check } from 'lucide-react';

export type CheckboxSize = 'md' | 'lg';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
    label?: string;
    description?: string;
    error?: string;
    size?: CheckboxSize;
}

interface CheckboxSizeClasses {
    row: string;
    box: string;
    check: string;
    label: string;
}

const SIZE_CLASSES: Record<CheckboxSize, CheckboxSizeClasses> = {
    md: { row: 'items-start', box: 'w-5 h-5', check: 'w-3.5 h-3.5', label: 'text-sm' },
    lg: { row: 'min-h-14 items-center', box: 'w-7 h-7', check: 'w-5 h-5', label: 'text-lg' },
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
    ({ label, description, error, className = '', disabled, size = 'md', ...props }, ref) => {
        const sizeClasses = SIZE_CLASSES[size];
        return (
            <div className={className}>
                <label className={`flex gap-3 ${sizeClasses.row} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                    <div className="relative flex items-center justify-center mt-0.5">
                        <input
                            ref={ref}
                            type="checkbox"
                            disabled={disabled}
                            className="peer sr-only"
                            {...props}
                        />
                        <div
                            className={`
                                ${sizeClasses.box} border-2 rounded transition-colors
                                peer-focus:ring-2 peer-focus:ring-primary peer-focus:ring-offset-2
                                peer-checked:bg-primary peer-checked:border-primary
                                ${error ? 'border-red-500' : 'border-gray-300'}
                                ${disabled ? 'bg-gray-100' : 'bg-white'}
                            `}
                        />
                        <Check
                            className={`absolute ${sizeClasses.check} text-white opacity-0 peer-checked:opacity-100 transition-opacity`}
                            strokeWidth={3}
                        />
                    </div>
                    <div className="flex-1">
                        {label && (
                            <span className={`${sizeClasses.label} font-medium ${disabled ? 'text-gray-400' : 'text-gray-700'}`}>
                                {label}
                            </span>
                        )}
                        {description && (
                            <p className={`text-sm ${disabled ? 'text-gray-300' : 'text-gray-500'}`}>
                                {description}
                            </p>
                        )}
                    </div>
                </label>
                {error && <p className="mt-1 text-sm text-red-500 ml-8">{error}</p>}
            </div>
        );
    }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
